import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import DOMPurify from 'dompurify';
import { Download, Loader2 } from 'lucide-react';
import { toPng } from 'html-to-image';
import { CarouselCardsPreview } from '../../carousel/CarouselCardsPreview';
import { carouselTemplateService } from '../../../services/carouselTemplateService';
import { RichModalEditor, domPurifyConfig, ensureFontsLoaded, extractPrimaryFontsFromHtml } from './RichModalEditor';

type CarouselCardItem = { id: string; html: string };

const hashString = (input: string) => {
  let hash = 5381;
  for (let i = 0; i < input.length; i++) {
    hash = (hash * 33) ^ input.charCodeAt(i);
  }
  return (hash >>> 0).toString(36);
};

const parseCardsJson = (carouselHtmlJson?: string): CarouselCardItem[] => {
  if (!carouselHtmlJson) return [];
  try {
    const parsed = JSON.parse(carouselHtmlJson);
    const arr = Array.isArray(parsed) ? (parsed as unknown[]) : [];
    const htmls = arr.filter((x): x is string => typeof x === 'string');
    return htmls.map((html, idx) => ({ id: `card_${idx}_${hashString(html)}`, html }));
  } catch {
    return [];
  }
};

interface CarouselCardsEditorProps {
  carouselHtmlJson?: string;
  onChange: (nextCarouselHtmlJson: string) => void;
}

export const CarouselCardsEditor: React.FC<CarouselCardsEditorProps> = ({ carouselHtmlJson, onChange }) => {
  const [items, setItems] = useState<CarouselCardItem[]>(() => parseCardsJson(carouselHtmlJson));
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaveTemplateOpen, setIsSaveTemplateOpen] = useState(false);
  const [templateName, setTemplateName] = useState('');
  const [isSavingTemplate, setIsSavingTemplate] = useState(false);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [editingHtml, setEditingHtml] = useState<string>('');
  const [isSaving, setIsSaving] = useState(false);
  const exportRootId = 'carousel-export-root';

  useEffect(() => {
    setItems(parseCardsJson(carouselHtmlJson));
  }, [carouselHtmlJson]);
  
  useEffect(() => {
    setCurrentIndex((prev) => {
      if (items.length === 0) return 0;
      return Math.max(0, Math.min(prev, items.length - 1));
    });
  }, [items]);

  const handleEditCardFromFullscreen = (idx: number) => {
    const safe = Math.max(0, Math.min(idx, items.length - 1));
    const current = items[safe];
    if (!current) return;
    setCurrentIndex(safe);
    setEditingHtml(current.html);
    setIsEditorOpen(true);
  };

  const handleDownloadCard = async (idx: number) => {
    if (isDownloading) return;
    setIsDownloading(true);
    try {
      const item = items[idx];
      if (!item) return;
      await ensureFontsLoaded(extractPrimaryFontsFromHtml(item.html));
      const element = document.getElementById(`${exportRootId}-${item.id}`);
      if (element instanceof HTMLElement) {
        const dataUrl = await toPng(element, { width: 1080, height: 1350 });
        const link = document.createElement('a');
        link.download = `card-${idx + 1}.png`;
        link.href = dataUrl;
        link.click();
      }
    } catch (err) {
      console.error('Failed to download image', err);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleSaveSelectedEditor = async (nextRawHtml: string) => {
    const current = items[currentIndex];
    if (!current) return;
    const id = current.id;
    setIsSaving(true);
    try {
      const next = DOMPurify.sanitize(nextRawHtml, domPurifyConfig) as unknown as string;
      setItems(prev => {
        const updated = prev.map(it => (it.id === id ? { ...it, html: next } : it));
        onChange(JSON.stringify(updated.map(x => x.html)));
        return updated;
      });
    } finally {
      setIsSaving(false);
      setIsEditorOpen(false);
    }
  };

  const cardsJsonForPreview = useMemo(() => JSON.stringify(items.map((x) => x.html)), [items]);

  if (items.length === 0) {
    return (
      <div className="text-sm text-gray-600 dark:text-gray-300">
        Карточек пока нет. Сгенерируйте их в чате попросив ИИ.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <CarouselCardsPreview
        carouselHtmlJson={cardsJsonForPreview}
        index={currentIndex}
        onIndexChange={setCurrentIndex}
        onEditCard={handleEditCardFromFullscreen}
      />

      <div className="flex justify-center">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setIsSaveTemplateOpen(true)}
            disabled={isSavingTemplate}
            className="flex items-center space-x-1 px-4 py-2 border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60 text-gray-900 dark:text-gray-100 rounded-lg text-xs font-medium transition-all shadow-sm active:scale-95"
          >
            <span>{isSavingTemplate ? 'Сохраняем…' : 'Сохранить как шаблон'}</span>
          </button>

          <button
            onClick={() => handleDownloadCard(currentIndex)}
            disabled={isDownloading}
            className="flex items-center space-x-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white rounded-lg text-xs font-medium transition-all shadow-sm active:scale-95"
          >
            {isDownloading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Download className="w-3 h-3" />}
            <span>{isDownloading ? 'Загрузка...' : 'Скачать'}</span>
          </button>
        </div>
      </div>

      <div id={exportRootId} className="fixed left-[-10000px] top-0 opacity-0 pointer-events-none" aria-hidden="true">
        {items.map((item) => (
          <div
            key={item.id} id={`${exportRootId}-${item.id}`}
            style={{ width: 1080, height: 1350 }}
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(item.html, domPurifyConfig) as unknown as string }}
          />
        ))}
      </div>

      <RichModalEditor
        open={isEditorOpen} html={editingHtml}
        onClose={() => setIsEditorOpen(false)}
        onSave={handleSaveSelectedEditor}
        isSaving={isSaving}
      />

      {isSaveTemplateOpen && ReactDOM.createPortal(
        <div className="fixed inset-0 bg-black/50 z-[21000] flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl max-w-md w-full p-5">
            <div className="text-base font-semibold text-gray-900 dark:text-gray-100 mb-2">Сохранить как шаблон</div>
            <div className="text-sm text-gray-600 dark:text-gray-300 mb-4">Шаблон сохранит все HTML-карточки.</div>
            <input
              value={templateName} onChange={e => setTemplateName(e.target.value)}
              placeholder="Название шаблона"
              className="w-full h-11 px-3 rounded-xl border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
            />
            <div className="mt-4 flex justify-end gap-2">
              <button type="button" onClick={() => setIsSaveTemplateOpen(false)} className="h-10 px-4 text-sm rounded-xl border border-gray-300">Отмена</button>
              <button
                type="button" disabled={isSavingTemplate || !templateName.trim()}
                onClick={async () => {
                  setIsSavingTemplate(true);
                  try {
                    await carouselTemplateService.saveTemplate({ name: templateName.trim(), htmlJson: cardsJsonForPreview });
                    setIsSaveTemplateOpen(false); setTemplateName('');
                  } catch (e) { console.error(e); } finally { setIsSavingTemplate(false); }
                }}
                className="h-10 px-4 text-sm rounded-xl bg-blue-600 text-white"
              >
                {isSavingTemplate ? 'Сохранение…' : 'Сохранить'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

